/**
 * Merge/export documents – API call via createAsyncThunk + documentsAPI.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { mergeExportDocuments as mergeExportDocumentsApi } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";
import { trackRequest } from "../../utils/requestTracker";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";

export const mergeExportDocuments = createAsyncThunk(
  "documents/mergeExport/mergeExportDocuments",
  async (
    { format, docs, mergeSessionId, userToken, downloadMode },
    { rejectWithValue }
  ) => {
    logger.info("mergeExportDocuments start", { format }, "REDUX");
    try {
      const tokenResponse = await new UserAuth().getUserToken(true);
      const accessToken = tokenResponse?.accessToken;
      if (!accessToken) {
        logger.error("mergeExportDocuments no access token", tokenResponse, "REDUX");
        return rejectWithValue(serializeError({ message: "No access token" }));
      }
      const result = await trackRequest("mergeExportDocuments", () =>
        mergeExportDocumentsApi(
          { format, docs, mergeSessionId, userToken, downloadMode },
          accessToken
        )
      );
      logger.info("mergeExportDocuments success", null, "REDUX");
      return result;
    } catch (error) {
      logger.error("mergeExportDocuments failed", error, "REDUX");
      return rejectWithValue(serializeError(error));
    }
  }
);

const mergeExportSlice = createSlice({
  name: "documents/mergeExport",
  initialState: {
    loading: false,
    error: null,
    result: null,
    lastRequest: null,
  },
  reducers: {
    resetMergeExport: (state) => {
      state.loading = false;
      state.error = null;
      state.result = null;
      state.lastRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(mergeExportDocuments.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.result = null;
        state.lastRequest = action.meta?.arg ?? null;
      })
      .addCase(mergeExportDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload ?? null;
      })
      .addCase(mergeExportDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
      });
  },
});

export const { resetMergeExport } = mergeExportSlice.actions;
export default mergeExportSlice.reducer;
